import React, {
	SyntheticEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { pieChart, plus } from '@/assets/icons';
import { LayoutContext } from '@/components/Layout';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { AsaphButton } from '@/components/shared/Button';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import {
	emptySelectedTags,
	FilterEnum,
	getSongFieldFromFilterEnum,
	SearchAndFilter,
	SearchAndFilterProps,
	ViewByValue,
} from '@/components/shared/SearchAndFilter';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { SortBar, SortBarProps, SortEnum } from '@/components/SortBar';
import { TeamSongCard } from '@/components/TeamSongCard';
import { Permissions } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { LimitedTeamSong, useTeamSongs } from '@/providers/TeamSongsProvider';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import { Songs360Modal } from './components/Songs360Modal';
import { SongsSettingsPanel } from './components/SongsSettingsPanel';
import styles from './Songs.module.scss';

type SortAndFilterState = {
	filters: Parameters<SearchAndFilterProps['onChange']>[0];
	sort: Parameters<SortBarProps['onChange']>[0];
};

export const Songs: React.FC = () => {
	const {
		openAddSongs,
		openAddCustomSong,
		openQuickAddSongs,
		openImportCsv,
	} = useLayout();
	const { teamSongs, isLoading, error, dataUpdatedAt } = useTeamSongs();
	const { setlists } = useSetlists();
	const lastPlayedBySongId = useMemo(() => {
		const map = new Map<string, number>();
		setlists
			.filter((s) => !s.draft && new Date(s.startAt) <= new Date())
			.forEach((s) => {
				const ts = new Date(s.startAt).getTime();
				s.teamSongIds.forEach((id) => {
					const existing = map.get(id) ?? 0;
					if (ts > existing) map.set(id, ts);
				});
			});
		return map;
	}, [setlists]);
	const { hasPermission } = usePermissions();
	const { enqueueSnackbar } = useSnackbar();
	const { isCopilot, user: currentUser } = useAuth();
	const { team } = useTeam();

	const [songsSettingsOpen, setSongsSettingsOpen] = useState(false);
	const [songs360ModalOpen, setSongs360ModalOpen] = useState(false);
	const [songsList, setSongsList] = useState<LimitedTeamSong[]>([]);
	const [clearFilters, setClearFilters] = useState(0);
	const [creatorFilter, setCreatorFilter] = useState<string[]>([]);
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const ACTIVE_ROTATION_STATUSES = new Set([
		'high',
		'regular',
		'low',
		'occasional',
	]);

	const [sortAndFilter, setSortAndFilter] = useState<SortAndFilterState>({
		filters: {
			searchTerm: '',
			selectedTags: emptySelectedTags,
			viewBy: 'active' as ViewByValue,
		},
		sort: SortEnum.TOP_RATED_TEAM,
	});
	const handleOpen = (e: SyntheticEvent) => {
		setAnchorEl(e.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const [searchFired, setSearchFired] = useState(false);
	const [title] = useFormattedTitle('Asaph | Songlist');
	const fireViewSongEvent = useAnalyticsEvent('Songlist_Open_View_Song');
	const fireOpenSonglist360Event = useAnalyticsEvent(
		'Songlist_Open_Songlist_360',
	);
	const fireEnterSearch = useAnalyticsEvent('Songlist_Enter_Search');

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const toTitleCase = (v: string) =>
		v.replace(/\b\w/g, (c) => c.toUpperCase());

	const filterOptions = useMemo(() => {
		const songs = teamSongs || [];
		return {
			[FilterEnum.ROTATION_STATUS]: (() => {
				const ROTATION_STATUS_ORDER = [
					'high',
					'regular',
					'low',
					'occasional',
					'seasonal',
					'rest',
				];
				return Array.from(
					new Set(songs.map((s) => s.rotationStatus || 'regular')),
				)
					.sort((a, b) => {
						const ai = ROTATION_STATUS_ORDER.indexOf(a);
						const bi = ROTATION_STATUS_ORDER.indexOf(b);
						if (ai === -1 && bi === -1) return a.localeCompare(b);
						if (ai === -1) return 1;
						if (bi === -1) return -1;
						return ai - bi;
					})
					.map((v) => ({ label: toTitleCase(v), value: v }));
			})(),
			[FilterEnum.ENERGY]: Array.from(
				new Set(songs.flatMap((s) => s.energyTags || [])),
			).map((v) => ({ label: toTitleCase(v), value: v })),
			[FilterEnum.SONG_TYPE]: Array.from(
				new Set(songs.flatMap((s) => s.songTypeTags || [])),
			).map((v) => ({ label: toTitleCase(v), value: v })),
			[FilterEnum.DEFAULT_KEY]: (() => {
				const KEY_ORDER = [
					'C',
					'Db',
					'D',
					'Eb',
					'E',
					'F',
					'F#',
					'G',
					'Ab',
					'A',
					'Bb',
					'B',
				];
				const unique = Array.from(
					new Set(
						songs
							.map((s) => s.defaultKey)
							.filter(Boolean) as string[],
					),
				);
				return unique
					.sort((a, b) => {
						const ai = KEY_ORDER.findIndex(
							(k) => k.toLowerCase() === a.toLowerCase(),
						);
						const bi = KEY_ORDER.findIndex(
							(k) => k.toLowerCase() === b.toLowerCase(),
						);
						if (ai === -1 && bi === -1) return a.localeCompare(b);
						if (ai === -1) return 1;
						if (bi === -1) return -1;
						return ai - bi;
					})
					.map((v) => ({ label: v, value: v }));
			})(),
		};
	}, [teamSongs]);

	const creatorOptions: { label: string; value: string }[] = [];

	const handleCreatorToggle = useCallback((value: string) => {
		setCreatorFilter((prev) =>
			prev.includes(value)
				? prev.filter((v) => v !== value)
				: [...prev, value],
		);
	}, []);

	const displayedSongsList = useMemo(() => songsList, [songsList]);

	const handleFilterChange: (sortAndFilter: SortAndFilterState) => void =
		useCallback(
			({ filters, sort }) => {
				const { searchTerm, selectedTags, viewBy } = filters;
				const allTeamSongs = teamSongs || [];

				// Apply viewBy pre-scope first
				const preScoped = allTeamSongs.filter((s) => {
					const status = (
						s.rotationStatus || 'regular'
					).toLowerCase();
					if (viewBy === 'active')
						return ACTIVE_ROTATION_STATUSES.has(status);
					if (viewBy === 'seasonal') return status === 'seasonal';
					return true; // 'all'
				});

				// This is the logic for deciding which songs to show to the user.
				setSongsList(
					// We iterate over each team song and match it based on different criteria
					preScoped
						.filter((s) => {
							// Match song name
							const nameMatch = s.name
								.toLocaleLowerCase()
								.includes(searchTerm.toLocaleLowerCase());
							// Match artist name
							const artistMatch = s.artist
								.toLocaleLowerCase()
								.includes(searchTerm.toLocaleLowerCase());
							const artistOrNameMatch = nameMatch || artistMatch;

							const hasSelectedTags = Object.values(
								selectedTags,
							).some((tags) => !!tags.length);

							if (!hasSelectedTags) {
								// If we don't have a tagsCategory then matching the song name or artist name is enough
								return artistOrNameMatch;
							}
							// This array defines the match for each tag category
							let tagsMatched: boolean[] = [];
							// Go over every selected category and its selected tags
							for (const [tagsCategory, tags] of Object.entries(
								selectedTags,
							)) {
								// If the user HAS NOT selected any tags from this category, consider it a match
								if (!tags.length) {
									tagsMatched.push(true);
									continue;
								}
								// If the user HAS selected tags from this category then get the song's category tags
								const tagsFromCategory =
									getSongFieldFromFilterEnum(
										s,
										tagsCategory as FilterEnum,
									);
								// If any (.some in JS words) of the tags
								tagsMatched.push(
									tagsFromCategory.some((songCategoryTag) =>
										tags.includes(songCategoryTag),
									),
								);
							}

							// Return only if the name and the tags match
							return (
								artistOrNameMatch &&
								tagsMatched.every((match) => match)
							);
						})
						.sort((a, b) => {
							const aOverall = a.feedback?.overallScore || 0;
							const bOverall = b.feedback?.overallScore || 0;
							const aLastPlayed =
								lastPlayedBySongId.get(a.id) ?? 0;
							const bLastPlayed =
								lastPlayedBySongId.get(b.id) ?? 0;

							switch (sort) {
								case SortEnum.TOP_RATED_TEAM:
									if (aOverall === bOverall) {
										return 0;
									}
									return aOverall > bOverall ? -1 : 1;
								case SortEnum.LAST_PLAYED:
									if (aLastPlayed === bLastPlayed) {
										return 0;
									}
									return aLastPlayed > bLastPlayed ? -1 : 1;
								default:
									if (a.name === b.name) {
										return 0;
									}
									return a.name > b.name ? 1 : -1;
							}
						}),
				);
				if (!searchFired && searchTerm) {
					fireEnterSearch();
					setSearchFired(true);
				}
			},
			// We need dataUpdatedAt in the deps array so that we can re-trigger the effect when the data changes
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[fireEnterSearch, searchFired, dataUpdatedAt, lastPlayedBySongId],
		);

	const handleSortAndFilter: (
		filters?: Parameters<SearchAndFilterProps['onChange']>[0],
		sort?: Parameters<SortBarProps['onChange']>[0],
	) => void = useCallback(
		(filters, sort) => {
			setSortAndFilter((state) => {
				const newSortAndFilter = {
					filters: filters || state.filters,
					sort: sort || state.sort,
				};
				handleFilterChange(newSortAndFilter);
				return newSortAndFilter;
			});
		},
		[handleFilterChange],
	);

	const searchOnChange: SearchAndFilterProps['onChange'] = useCallback(
		(filters) => {
			handleSortAndFilter(filters);
		},
		[handleSortAndFilter],
	);

	const sortBarOnChange: SortBarProps['onChange'] = useCallback(
		(sort) => {
			handleSortAndFilter(undefined, sort);
		},
		[handleSortAndFilter],
	);

	useEffect(() => {
		handleFilterChange(sortAndFilter);
		// We need to re-run the filter logic when the data updates
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataUpdatedAt, handleFilterChange, sortAndFilter]);

	if (error || (!isLoading && !teamSongs)) {
		console.error(error);
		enqueueSnackbar('Error while loading songs', { variant: 'error' });
	}

	const noSongs = !teamSongs.length;
	const open = !!anchorEl;

	const dropdownItems = [
		{
			label: 'Add from library',
			onClick: () => {
				openAddSongs();
				handleClose();
			},
		},
		{
			label: 'Import from CSV',
			onClick: () => {
				openImportCsv();
				handleClose();
			},
		},
		{
			label: 'Create new song',
			onClick: () => {
				openAddCustomSong();
				handleClose();
			},
		},
	];

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				titleComponent="Your songs"
				actions={[
					{
						icon: pieChart,
						onClick: () => {
							setSongs360ModalOpen(true);
							fireOpenSonglist360Event();
						},
					},
					{
						icon: plus,
						onClick: handleOpen,
						hidden: !hasPermission(Permissions.SonglistUpdate),
					},
				]}
				topComponent={
					<SearchAndFilter
						className={styles['search-bar']}
						onChange={searchOnChange}
						clearFilters={clearFilters}
						noPadding
						placeholder="Search song title or artist"
						filterOptions={filterOptions}
						songs={teamSongs || []}
						creatorOptions={
							creatorOptions.length > 1
								? creatorOptions
								: undefined
						}
						creatorFilter={creatorFilter}
						onCreatorToggle={handleCreatorToggle}
					/>
				}
				isLoading={isLoading}
			>
				<div
					className={classNames([
						styles['songs'],
						(noSongs || !displayedSongsList.length) &&
							styles['full-height'],
					])}
				>
					{noSongs ? (
						hasPermission(Permissions.TeamUpdate) ? (
							<EmptyResourceMessage
								onActionClick={openQuickAddSongs}
								title="There are no songs"
								subtitle="Add songs to create your Songlist"
								actionLabel="Add Songs"
							/>
						) : (
							<EmptyResourceMessage
								title="There are no songs"
								subtitle="Songs will appear here when admins add them to your Songlist"
							/>
						)
					) : (
						<>
							{displayedSongsList.length ? (
								<div>
									<SortBar
										defaultSort={
											isCopilot
												? SortEnum.ALPHANUMERIC
												: undefined
										}
										onChange={sortBarOnChange}
									/>
									{displayedSongsList.map((song) => (
										<TeamSongCard
											key={song.id}
											teamSongId={song.id}
											onClick={(teamSong) => {
												fireViewSongEvent({
													id: teamSong.id,
													artist: teamSong.artist,
													title: teamSong.name,
												});
											}}
											light
											negativeSpacing
											displayTrend={
												sortAndFilter.sort !==
												SortEnum.LAST_PLAYED
											}
											displayOverallScore={
												sortAndFilter.sort ===
												SortEnum.TOP_RATED_TEAM
											}
											displayKey={
												sortAndFilter.sort ===
												SortEnum.ALPHANUMERIC
											}
											displayLastPlayed={
												sortAndFilter.sort ===
												SortEnum.LAST_PLAYED
											}
											displayRotationStatus={
												sortAndFilter.sort ===
												SortEnum.LAST_PLAYED
											}
											lastPlayedISO={
												lastPlayedBySongId.get(song.id)
													? new Date(
															lastPlayedBySongId.get(
																song.id,
															)!,
													  ).toISOString()
													: undefined
											}
										/>
									))}
								</div>
							) : (
								<EmptyResourceMessage
									title={
										sortAndFilter.filters.searchTerm
											? `"${sortAndFilter.filters.searchTerm}" not found`
											: 'No songs found'
									}
									className="w-100"
									subtitle="Your filters have returned 0 results"
									buttonComponent={
										<div
											className={
												styles['empty-resource-buttons']
											}
										>
											<AsaphButton
												fullWidth
												label="Clear filters"
												color="grey"
												onClick={() => {
													setClearFilters(
														clearFilters + 1,
													);
													setCreatorFilter([]);
												}}
											/>
											<AsaphButton
												fullWidth
												label="Add songs"
												color="blue"
												onClick={openQuickAddSongs}
											/>
										</div>
									}
								/>
							)}
						</>
					)}
				</div>
			</SheetLayout>

			<SongsSettingsPanel
				open={songsSettingsOpen}
				setOpen={setSongsSettingsOpen}
			/>
			<Songs360Modal
				open={songs360ModalOpen}
				onClose={() => setSongs360ModalOpen(false)}
				songIds={displayedSongsList.map((song) => song.id)}
			/>

			<AsaphDropdownMenu
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				items={dropdownItems}
			/>
		</>
	);
};
